// ============================================================
// DM-LMS — Shared Validation Schemas (Zod)
// DRY: one schema used by both API (Hono) and Web (React Hook Form).
// ============================================================

import { z } from "zod";
import {
  ROLES,
  DELIVERY_MODES,
  CONFIG_CATEGORIES,
  PROFESSIONS,
  PREFERRED_PAYMENT_METHODS,
  SPECIALISATIONS,
  ACTIVITY_EVENT_TYPES,
  TRANSACTION_STATUSES,
  ENROLLMENT_STATUSES,
} from "../types/index.js";
import {
  PAYMENT_REFERENCE_LENGTH,
  TUTOR_EMAIL_MAX_LENGTH,
  COUNTRY_CODES,
  KENYA_COUNTRY_CODE,
  STUDENT_PHONE_PATTERN,
  KENYAN_PHONE_PATTERN,
  VALIDATION_MESSAGES,
  PERSON_NAME_MIN_LENGTH,
  PERSON_NAME_MAX_LENGTH,
  USER_SEARCH_MAX_LENGTH,
  PROOF_TYPE_CASH,
  PROOF_TYPE_MPESA,
  PROOF_PARTY_LENGTH,
  PROOF_REFERENCE_LENGTH,
} from "../constants/index.js";
import { deriveTutorEmailKey, normaliseEmail, normaliseProofReference } from "../utils/index.js";

// ---- Primitives ----
export const phoneSchema = z
  .string()
  .regex(KENYAN_PHONE_PATTERN, VALIDATION_MESSAGES.kenyanPhone);

// Student phone: international format (non-Kenyan students can register).
export const studentPhoneSchema = z
  .string()
  .regex(STUDENT_PHONE_PATTERN, VALIDATION_MESSAGES.studentPhone);

export const codeSchema = z
  .string()
  .regex(/^[A-Z]+-\d{4}$/, "Invalid entity code format");

// ---- Course ----
export const createCourseSchema = z.object({
  title:          z.string().min(3).max(200),
  description:    z.string().min(10).max(2000),
  // Optional here — a tutor creating their own course is forced to their own
  // id server-side; an admin creating on a tutor's behalf must supply it.
  tutorId:        z.string().uuid().optional(),
  mode:           z.enum(DELIVERY_MODES),
  capacity:       z.number().int().min(1).max(200),
  prerequisiteId: z.string().uuid().nullable(),
  durationHours:  z.number().int().min(1).max(500),
  price:          z.number().int().min(0),             // KES, no decimals
});

export const updateCourseSchema = createCourseSchema.partial().extend({
  isPublished: z.boolean().optional(),
  // M-Pesa account-reference prefix (e.g. "MF"); null clears it, falling back to the course code.
  accountRef:  z.string().min(1).max(20).nullable().optional(),
});

// ---- Session ----
export const createSessionSchema = z.object({
  courseId:        z.string().uuid(),
  title:           z.string().min(3).max(200),
  scheduledAt:     z.string().datetime(),
  durationMinutes: z.number().int().min(15).max(480),
  mode:            z.enum(DELIVERY_MODES),
  meetingUrl:      z.string().url().nullable(),
  venue:           z.string().max(500).nullable(),
  // Override only — omitted, the session inherits the course's tutor (resolveTutorId).
  tutorId:         z.string().uuid().optional(),
});

// courseId is optional — omitted, a tutor lists sessions across all of their
// own courses (admin sees all); present, it lists that one course's sessions.
export const sessionsQuerySchema = z.object({
  courseId: z.string().uuid().optional(),
});

// ---- Enrollment ----
export const enrollSchema = z.object({
  courseId: z.string().uuid(),
  // Optional — a student enrolling themself is forced to their own id
  // server-side; an admin's manual enrollment form must supply it.
  studentId: z.string().uuid().optional(),
});

// The enrollment lifecycle status (active/paused/completed/dropped). Payment status has no schema: it is
// never edited by hand, only set when a payment is confirmed (settlePayment).
export const updateEnrollmentLifecycleStatusSchema = z.object({
  status: z.enum(ENROLLMENT_STATUSES),
});

// ---- Attendance ----
export const checkInSchema = z.object({
  sessionId: z.string().uuid(),
  studentId: z.string().uuid(),
});

// ---- Feedback ----
export const feedbackQuerySchema = z.object({
  sessionId: z.string().uuid(),
});

export const createFeedbackSchema = z.object({
  tutorId:   z.string().uuid(),
  studentId: z.string().uuid(),
  sessionId: z.string().uuid(),
  notes:     z.string().min(1).max(2000),
  isPrivate: z.boolean(),
});

// ---- Materials ----
export const materialsQuerySchema = z.object({
  courseId: z.string().uuid(),
});

// ---- Payment ----
export const initiateMpesaSchema = z.object({
  enrollmentId: z.string().uuid(),
  phone:        phoneSchema,
  amountKes:    z.number().int().min(1),
});

export const initiateStripeSchema = z.object({
  enrollmentId: z.string().uuid(),
  amountKes:    z.number().int().min(1),
});

// M-Pesa transaction code as typed by a student or admin (normalised to uppercase before storing/comparing)
const mpesaCodeSchema = z
  .string()
  .trim()
  .min(PAYMENT_REFERENCE_LENGTH.min, VALIDATION_MESSAGES.mpesaCode)
  .max(PAYMENT_REFERENCE_LENGTH.max, VALIDATION_MESSAGES.mpesaCode)
  .regex(/^[A-Za-z0-9]+$/, VALIDATION_MESSAGES.mpesaCode);

export const submitPaymentReferenceSchema = z.object({
  reference: mpesaCodeSchema,
});

export const updatePaymentStatusSchema = z.object({
  status: z.enum(TRANSACTION_STATUSES),
});

// ---- Student Profile ----
// PATCH semantics: every field is optional (an omitted field keeps its saved value), so the
// "everything required" rule lives in isStudentProfileComplete. The cross-field rule below applies
// whenever a request names a country: Kenya needs a county, anywhere else needs a region.
export const studentProfileInputSchema = z
  .object({
    name:                     z.string().trim().min(PERSON_NAME_MIN_LENGTH).max(PERSON_NAME_MAX_LENGTH).optional(),
    phone:                    studentPhoneSchema.optional(),
    id_number:                z.string().min(1).optional(),
    organization:             z.string().trim().min(1).optional(),
    profession:               z.enum(PROFESSIONS).optional(),
    country:                  z.enum(COUNTRY_CODES).optional(),
    county:                   z.string().optional(),
    region:                   z.string().optional(),
    preferred_payment_method: z.enum(PREFERRED_PAYMENT_METHODS).optional(),
    mpesa_number:             z.string().optional(),
    bio:                      z.string().max(500).optional(),
  })
  .superRefine((input, ctx) => {
    if (input.country === undefined) return;
    const inKenya = input.country === KENYA_COUNTRY_CODE;
    const field = inKenya ? "county" : "region";
    if ((input[field] ?? "").trim() === "") {
      ctx.addIssue({
        code:    z.ZodIssueCode.custom,
        path:    [field],
        message: inKenya ? VALIDATION_MESSAGES.countyRequired : VALIDATION_MESSAGES.regionRequired,
      });
    }
  });

// ---- Tutor Profile ----
export const tutorProfileInputSchema = z.object({
  phone:          phoneSchema.optional(),
  specialisation: z.enum(SPECIALISATIONS).optional(),
  qualifications: z.string().optional(),
  bio:            z.string().max(500).optional(),
  photo_r2_key:   z.string().optional(),
});

// ---- Admin Profile ----
export const adminProfileInputSchema = z.object({
  phone: phoneSchema.optional(),
});

// ---- Pagination ----
export const paginationSchema = z.object({
  page:     z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
});

// Admin payment ledger: paginated, optionally filtered by status and/or one student. Strict: a parameter the
// ledger does not know is a 400, never silently dropped (a dropped studentId once listed every student's payments).
export const paymentLedgerQuerySchema = paginationSchema
  .extend({
    status:    z.enum(TRANSACTION_STATUSES).optional(),
    studentId: z.string().uuid().optional(),
  })
  .strict();

// ---- System config ----
// Lowercase letters, digits, underscores — may start with a digit (e.g. court_clerk, 150 for session_duration).
// Also checked client-side by the admin form.
export const CONFIG_KEY_PATTERN = /^[a-z0-9][a-z0-9_]*$/;

// ---- Payment verification ----
// Admin verifies against the code on their M-Pesa statement, or confirms manually when the student
// never submitted one (an explicit flag — never a magic code value). A manual confirmation carries
// proof: cash needs payer, receiver and amount; every other type needs a reference. Shape only — whether
// the type is an active config row, the amount equals the payment's and the reference is unused is the server's job.
const proofPartySchema = z
  .string()
  .trim()
  .min(PROOF_PARTY_LENGTH.min, VALIDATION_MESSAGES.proofParty)
  .max(PROOF_PARTY_LENGTH.max, VALIDATION_MESSAGES.proofParty);

// The amount the admin received, required for EVERY manual proof type. Shape only (a whole, positive number
// of KES); that it equals the payment's amount is checked once, in the verify route.
const proofAmountSchema = z
  .number({ required_error: VALIDATION_MESSAGES.proofAmount, invalid_type_error: VALIDATION_MESSAGES.proofAmount })
  .int(VALIDATION_MESSAGES.proofAmount)
  .positive(VALIDATION_MESSAGES.proofAmount);

const manualCashProofSchema = z.object({
  manual:    z.literal(true),
  proofType: z.literal(PROOF_TYPE_CASH),
  payer:     proofPartySchema,
  receiver:  proofPartySchema,
  amountKes: proofAmountSchema,
});

// The reference is normalised here (whitespace stripped, uppercased) whatever the client sent. An M-Pesa
// proof must then pass the same format rules as a student's code (mpesaCodeSchema); every other type is
// normalised but not format-checked.
const manualReferenceProofSchema = z
  .object({
    manual:    z.literal(true),
    proofType: z.string().max(50).regex(CONFIG_KEY_PATTERN, "Invalid proof type"),
    reference: z
      .string()
      .transform(normaliseProofReference)
      .pipe(z.string().min(PROOF_REFERENCE_LENGTH.min, VALIDATION_MESSAGES.proofReference).max(PROOF_REFERENCE_LENGTH.max, VALIDATION_MESSAGES.proofReference)),
    amountKes: proofAmountSchema,
  })
  .superRefine((input, ctx) => {
    if (input.proofType === PROOF_TYPE_MPESA && !mpesaCodeSchema.safeParse(input.reference).success) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["reference"], message: VALIDATION_MESSAGES.mpesaCode });
    }
  });

const adminCodeSchema = z.object({ adminCode: mpesaCodeSchema });

export type ManualProofInput = z.infer<typeof manualCashProofSchema> | z.infer<typeof manualReferenceProofSchema>;

// One schema picks its branch by shape, so a failure reports THAT branch's issues. (z.union would answer
// every bad body with "Invalid input", hiding the clear message.)
export const verifyPaymentSchema = z.unknown().transform((input, ctx): z.infer<typeof adminCodeSchema> | ManualProofInput => {
  const record = typeof input === "object" && input !== null ? (input as Record<string, unknown>) : {};
  const branch =
    "adminCode" in record ? adminCodeSchema : record.proofType === PROOF_TYPE_CASH ? manualCashProofSchema : manualReferenceProofSchema;
  const parsed = branch.safeParse(input);
  if (!parsed.success) {
    for (const issue of parsed.error.issues) ctx.addIssue(issue);
    return z.NEVER;
  }
  return parsed.data;
});

export const configCategorySchema = z.enum(CONFIG_CATEGORIES);

export const configQuerySchema = z.object({
  category: configCategorySchema,
});

// The single definition of a valid tutor email, used by the server (create) and the admin form.
export type TutorEmailResult = { ok: true; email: string } | { ok: false; error: string };

export function parseTutorEmail(input: string): TutorEmailResult {
  const email = normaliseEmail(input);
  if (email.length > TUTOR_EMAIL_MAX_LENGTH) {
    return { ok: false, error: `Email must be ${TUTOR_EMAIL_MAX_LENGTH} characters or fewer` };
  }
  if (!z.string().email().safeParse(email).success) return { ok: false, error: "Enter a valid email address" };
  return { ok: true, email };
}

// `key` is required except for tutor_email, where the server derives it from the (normalised)
// label and ignores any supplied key. The output always carries a key.
export const createConfigSchema = z
  .object({
    category:   configCategorySchema,
    key:        z.string().min(1).max(50).regex(CONFIG_KEY_PATTERN, "Key must be lowercase letters, digits and underscores").optional(),
    label:      z.string().min(1).max(100),
    sort_order: z.number().int().optional(),
    metadata:   z.record(z.unknown()).optional(),
  })
  .transform(async (input, ctx) => {
    if (input.category === "tutor_email") {
      const parsed = parseTutorEmail(input.label);
      if (!parsed.ok) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["label"], message: parsed.error });
        return z.NEVER;
      }
      return { ...input, label: parsed.email, key: await deriveTutorEmailKey(parsed.email) };
    }
    if (input.key === undefined) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["key"], message: "Key is required" });
      return z.NEVER;
    }
    return { ...input, key: input.key };
  });

export const updateConfigSchema = z.object({
  label:      z.string().min(1).max(100).optional(),
  sort_order: z.number().int().optional(),
  is_active:  z.boolean().optional(),
  metadata:   z.record(z.unknown()).optional(),
});

// ---- Admin: Issue certificate directly (not via eligibility check) ----
export const issueCertificateSchema = z.object({
  studentId: z.string().uuid(),
  courseId:  z.string().uuid(),
});

// ---- Admin: Activity Log ----
export const activityQuerySchema = paginationSchema.extend({
  eventType: z.enum(ACTIVITY_EVENT_TYPES).optional(),
});

// ---- Role ----
export const roleSchema = z.enum(ROLES);

// ---- Admin: Users list ----
export const usersQuerySchema = paginationSchema.extend({
  role: roleSchema.optional(),
  // Trimmed, so a blank box is rejected rather than matching everyone; the UI omits an empty q.
  q:    z.string().trim().min(1).max(USER_SEARCH_MAX_LENGTH).optional(),
});

// ---- Admin: delete a user (?confirmCode= is the target's code, typed by the admin) ----
export const deleteUserQuerySchema = z.object({
  confirmCode: z.string().trim().toUpperCase().min(1).max(20),
});

// ---- Inferred types (DRY — derive types from schemas) ----
export type CreateCourseInput     = z.infer<typeof createCourseSchema>;
export type UpdateCourseInput     = z.infer<typeof updateCourseSchema>;
export type CreateSessionInput    = z.infer<typeof createSessionSchema>;
export type SessionsQueryInput    = z.infer<typeof sessionsQuerySchema>;
export type EnrollInput           = z.infer<typeof enrollSchema>;
export type CheckInInput          = z.infer<typeof checkInSchema>;
export type FeedbackQueryInput    = z.infer<typeof feedbackQuerySchema>;
export type CreateFeedbackInput   = z.infer<typeof createFeedbackSchema>;
export type MaterialsQueryInput   = z.infer<typeof materialsQuerySchema>;
export type StudentProfileInput   = z.infer<typeof studentProfileInputSchema>;
export type TutorProfileInput     = z.infer<typeof tutorProfileInputSchema>;
export type AdminProfileInput     = z.infer<typeof adminProfileInputSchema>;
export type UpdatePaymentStatusInput = z.infer<typeof updatePaymentStatusSchema>;
export type InitiateMpesaInput    = z.infer<typeof initiateMpesaSchema>;
export type InitiateStripeInput   = z.infer<typeof initiateStripeSchema>;
export type PaginationInput       = z.infer<typeof paginationSchema>;
export type CreateConfigInput     = z.infer<typeof createConfigSchema>;
export type UpdateConfigInput     = z.infer<typeof updateConfigSchema>;
export type ActivityQueryInput    = z.infer<typeof activityQuerySchema>;
export type UsersQueryInput       = z.infer<typeof usersQuerySchema>;
export type DeleteUserQueryInput  = z.infer<typeof deleteUserQuerySchema>;
export type UpdateEnrollmentLifecycleStatusInput = z.infer<typeof updateEnrollmentLifecycleStatusSchema>;
export type IssueCertificateInput = z.infer<typeof issueCertificateSchema>;
